import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { timer } from "@/utils/index";
import { forgetPassword, getToken } from "@/Api/user";
import { authorizeUser, logout } from "@/hooks/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Form } from "@/components/Form";
import * as Icons from "@/components/Icons";
import { LoaderSpin } from "@/components/Loaders";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const schema = z.object({
  email: z.string({ message: "*requerido" }).email("*Formato invalido"),
  password: z
    .string({ message: "*requerido" })
    .min(3, "*Mínimo de 3 caracteres"),
});

const forgotPasswordSchema = z.object({
  email: z.string({ message: "*requerido" }).email("*Formato invalido"),
});

type schemaType = z.infer<typeof schema>;
type forgotPasswordSchemaType = z.infer<typeof forgotPasswordSchema>;

export function LoginForm() {
  const [searchParams] = useSearchParams();
  const isAddingAccount = searchParams.get("addAccount") === "true";

  useEffect(() => {
    if (!isAddingAccount) {
      logout();
    }
  }, [isAddingAccount]);

  const redirect = useNavigate();

  const form = useForm<schemaType>({
    resolver: zodResolver(schema),
  });

  const forgotPasswordForm = useForm<forgotPasswordSchemaType>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const [formState, setFormState] = useState<string>("default");
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] =
    useState(false);
  const [forgotPasswordState, setForgotPasswordState] =
    useState<string>("default");

  const handleSubmit = async (data) => {
    setFormState("loading");

    const {
      ok,
      data: { response },
    } = await getToken(data);

    if (!ok || !response?.token) {
      setFormState("error");
      await timer();
      setFormState("default");
      return;
    }

    authorizeUser({
      token: response?.token,
      user: response.user,
    });

    redirect("/dashboard");
  };

  const handleForgotPassword = async () => {
    setForgotPasswordState("loading");
    const { ok, data: dataForgot } = await forgetPassword(
      forgotPasswordForm.getValues("email")
    );

    if (!ok) {
      setForgotPasswordState("error");
      await timer();
      setForgotPasswordState("default");
      return;
    }
    toast.success(dataForgot?.message);

    setForgotPasswordState("success");
    await timer(1000);
    setForgotPasswordState("default");
    setForgotPasswordDialogOpen(false);
  };

  return (
    <>
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        {...form}
        className="max-w-md w-full"
      >
        <Form.Row>
          <Form.Field name="email">
            {({ field }) => (
              <Form.Item>
                <Form.Label>
                  Email <Form.Message />
                </Form.Label>
                <Form.Control>
                  <Input {...field} placeholder="seuemail@exemplo.com" />
                </Form.Control>
              </Form.Item>
            )}
          </Form.Field>
        </Form.Row>
        <Form.Row>
          <Form.Field name="password">
            {({ field }) => (
              <Form.Item>
                <Form.Label className="flex justify-between items-center">
                  <span>
                    Senha <Form.Message />
                  </span>
                </Form.Label>
                <Form.Control>
                  <Input
                    typeMask="password"
                    {...field}
                    placeholder="Insira sua senha"
                  />
                </Form.Control>
              </Form.Item>
            )}
          </Form.Field>
        </Form.Row>
        <Button
          type="button"
          variant="link"
          className="text-sm text-black dark:text-white ml-auto hover:underline p-0"
          onClick={() => setForgotPasswordDialogOpen(true)}
        >
          Esqueceu a senha?
        </Button>
        <Form.Footer>
          <Button
            className="w-full bg-primary"
            type="submit"
            disabled={formState != "default"}
          >
            {formState == "loading" && (
              <>
                Logando
                <LoaderSpin />
              </>
            )}
            {formState == "default" && "Entrar"}
            {formState == "error" && (
              <>
                Error
                <Icons.Error className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </Form.Footer>
      </Form>

      <Dialog
        open={forgotPasswordDialogOpen}
        onOpenChange={setForgotPasswordDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recuperar senha</DialogTitle>
            <DialogDescription>
              Insira seu email para receber instruções de recuperação de senha.
            </DialogDescription>
          </DialogHeader>
          <Form
            onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)}
            {...forgotPasswordForm}
          >
            <Form.Row>
              <Form.Field name="email">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Email <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="seuemail@exemplo.com" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            <DialogFooter>
              <Button
                type="submit"
                disabled={forgotPasswordState != "default"}
                className="w-full"
              >
                {forgotPasswordState == "loading" && (
                  <>
                    Enviando <LoaderSpin />
                  </>
                )}
                {forgotPasswordState == "default" && "Enviar instruções"}
                {forgotPasswordState == "error" && (
                  <>
                    Erro <Icons.Error className="ml-2 h-4 w-4" />
                  </>
                )}
                {forgotPasswordState == "success" && "Enviado com sucesso"}
              </Button>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
